// src/pages/StudiesPage.js
//
// Ticket 13: org-admin UI for managing the studies in the active org.
// Lists active + archived studies, supports create / rename / archive.
// Clicking "Open" in a row sets the active study (via SessionContext)
// and routes back to the admin dashboard scoped to that study.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Plus,
  Archive,
  ArchiveRestore,
  Pencil,
  CheckCircle,
  Loader,
  AlertTriangle,
} from 'lucide-react';
import { useSession } from '../auth/sessionContext';
import {
  loadAllStudiesForOrg,
  createStudy,
  renameStudy,
  archiveStudy,
  unarchiveStudy,
} from '../data/studiesHelpers';

const StudiesPage = () => {
  const navigate = useNavigate();
  const {
    user,
    activeOrg,
    activeStudy,
    setActiveStudy,
    reloadStudiesForActiveOrg,
  } = useSession();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const load = async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const all = await loadAllStudiesForOrg(activeOrg.orgId);
      setRows(all);
    } catch (err) {
      setStatus({ type: 'error', message: `Failed to load studies: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg]);

  const visible = rows.filter((r) => showArchived || !r.archived);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      setStatus({ type: 'error', message: 'Study name is required.' });
      return;
    }
    setCreating(true);
    setStatus({ type: 'loading', message: 'Creating study...' });
    try {
      await createStudy({
        orgId: activeOrg.orgId,
        name: newName.trim(),
        description: newDescription.trim(),
        createdBy: user?.uid || 'unknown',
      });
      setNewName('');
      setNewDescription('');
      await load();
      await reloadStudiesForActiveOrg();
      setStatus({ type: 'success', message: 'Study created.' });
    } catch (err) {
      setStatus({ type: 'error', message: `Create failed: ${err.message}` });
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (study) => {
    if (!window.confirm(`Archive "${study.name}"? It will hide from reviewer sidebars until you unarchive.`)) return;
    try {
      await archiveStudy(study.id);
      await load();
      await reloadStudiesForActiveOrg();
      setStatus({ type: 'success', message: `Archived "${study.name}".` });
    } catch (err) {
      setStatus({ type: 'error', message: `Archive failed: ${err.message}` });
    }
  };

  const handleUnarchive = async (study) => {
    try {
      await unarchiveStudy(study.id);
      await load();
      await reloadStudiesForActiveOrg();
      setStatus({ type: 'success', message: `Unarchived "${study.name}".` });
    } catch (err) {
      setStatus({ type: 'error', message: `Unarchive failed: ${err.message}` });
    }
  };

  const handleStartEdit = (study) => {
    setEditingId(study.id);
    setEditingName(study.name);
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim()) return;
    try {
      await renameStudy(editingId, { name: editingName.trim() });
      setEditingId(null);
      setEditingName('');
      await load();
      await reloadStudiesForActiveOrg();
      setStatus({ type: 'success', message: 'Renamed.' });
    } catch (err) {
      setStatus({ type: 'error', message: `Rename failed: ${err.message}` });
    }
  };

  const handleOpen = (study) => {
    setActiveStudy(study.id);
    navigate('/admin/dashboard');
  };

  const statusStyles = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800',
  };

  if (!activeOrg) {
    return (
      <main className="p-4 lg:p-8">
        <div className="bg-white p-6 rounded-xl border border-slate-200 max-w-2xl">
          No active organization.
        </div>
      </main>
    );
  }

  return (
    <main className="p-4 lg:p-8 pb-32">
      <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg max-w-3xl">
        <h2 className="text-2xl font-bold text-slate-800 mb-1 flex items-center gap-2">
          <BookOpen size={24} /> Studies
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Studies in <strong>{activeOrg.orgName}</strong>. Each study owns its
          own questionnaire, papers, and reviewer assignments.
        </p>

        {status.message && (
          <div className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-3 ${statusStyles[status.type]}`}>
            {status.type === 'error' && <AlertTriangle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'loading' && <Loader size={16} className="animate-spin" />}
            <span>{status.message}</span>
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <label className="text-sm text-slate-600 flex items-center gap-2">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>

        {loading ? (
          <div className="py-8 text-slate-500 flex items-center gap-3">
            <Loader size={20} className="animate-spin" /> Loading studies...
          </div>
        ) : visible.length === 0 ? (
          <div className="py-8 text-slate-500 italic">
            {showArchived ? 'No studies.' : 'No active studies — create your first below.'}
          </div>
        ) : (
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
            {visible.map((study) => {
              const isActive = activeStudy && activeStudy.studyId === study.id;
              return (
                <li key={study.id} className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    {editingId === study.id ? (
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="w-full p-2 border border-slate-300 rounded-lg text-sm"
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">
                          {study.name}
                        </span>
                        {isActive && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-medium">
                            active
                          </span>
                        )}
                        {study.archived && (
                          <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full font-medium">
                            archived
                          </span>
                        )}
                      </div>
                    )}
                    {study.description && !editingId && (
                      <div className="text-xs text-slate-500 mt-1">{study.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {editingId === study.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditingName(''); }}
                          className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-200"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {!study.archived && (
                          <button
                            onClick={() => handleOpen(study)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700"
                          >
                            Open
                          </button>
                        )}
                        <button
                          onClick={() => handleStartEdit(study)}
                          title="Rename"
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                        >
                          <Pencil size={14} />
                        </button>
                        {study.archived ? (
                          <button
                            onClick={() => handleUnarchive(study)}
                            title="Unarchive"
                            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                          >
                            <ArchiveRestore size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleArchive(study)}
                            title="Archive"
                            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                          >
                            <Archive size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <form
          onSubmit={handleCreate}
          className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3"
        >
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Plus size={18} /> New study
          </h3>
          <div>
            <label className="block text-sm text-slate-600 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="ML Interpretability Review 2026"
              className="w-full p-2 border border-slate-300 rounded-lg text-sm focus-academic"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">
              Description
            </label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Optional free-text context for reviewers."
              className="w-full p-2 border border-slate-300 rounded-lg text-sm focus-academic"
              rows={2}
            />
          </div>
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold disabled:bg-blue-300"
          >
            {creating ? 'Creating...' : 'Create study'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default StudiesPage;
